import { useState } from "react";
import { Wand2, Skull, Map, Search, UserCog, ArrowRight, AlertTriangle, CheckCircle2, Save, Target } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAppMode } from "@/context/AppModeContext";
import { Loader, Bullets, Chips, SectionCard, SpeakButton } from "@/components/common";

const TOOLS = [
  { key: "reality-check", label: "Reality Check", icon: Skull, tagline: "Brutally honest analysis of any plan, idea or decision.", placeholder: "Describe your plan, idea or decision…", brutalOpt: false },
  { key: "life-path", label: "Life Path", icon: Map, tagline: "Turn a big goal into a living, personalised plan.", placeholder: "e.g. I want to become financially independent", brutalOpt: false },
  { key: "what-am-i-missing", label: "What Am I Missing?", icon: Search, tagline: "Find your blind spots, missing prerequisites and hidden risks.", placeholder: "Describe what you're planning or considering…", brutalOpt: false },
  { key: "make-it-fit", label: "Make It Fit My Life", icon: UserCog, tagline: "Personalise generic advice to your real constraints.", placeholder: "Paste advice + your constraints (time, budget, situation)…", brutalOpt: false },
];

function Score({ value, label }) {
  return (
    <div className="flex items-center gap-3">
      <div className="relative h-14 w-14 shrink-0">
        <svg viewBox="0 0 36 36" className="h-14 w-14 -rotate-90">
          <circle cx="18" cy="18" r="15" fill="none" stroke="hsl(var(--secondary))" strokeWidth="4" />
          <circle cx="18" cy="18" r="15" fill="none" stroke="hsl(var(--primary))" strokeWidth="4" strokeDasharray={`${(value/100)*94} 94`} strokeLinecap="round" />
        </svg>
        <span className="absolute inset-0 grid place-items-center font-head font-extrabold">{value}</span>
      </div>
      <span className="font-head font-semibold">{label}</span>
    </div>
  );
}

function Result({ tool, data }) {
  if (tool === "reality-check") return (
    <>
      {data.verdict && <SectionCard title="Verdict" accent><p className="text-lg">{data.verdict}</p><SpeakButton text={data.verdict} className="mt-3" /></SectionCard>}
      {typeof data.reality_score === "number" && <SectionCard title="Reality score"><Score value={data.reality_score} label="Realistic viability" /></SectionCard>}
      {data.what_could_go_wrong?.length > 0 && <SectionCard title="What could go wrong" icon={AlertTriangle}><Bullets items={data.what_could_go_wrong} /></SectionCard>}
      {data.hidden_costs?.length > 0 && <SectionCard title="Hidden costs"><Bullets items={data.hidden_costs} /></SectionCard>}
      {data.assumptions?.length > 0 && <SectionCard title="Shaky assumptions"><Bullets items={data.assumptions} /></SectionCard>}
      {data.competitors_or_alternatives?.length > 0 && <SectionCard title="What already exists"><Bullets items={data.competitors_or_alternatives} /></SectionCard>}
      {data.strengths?.length > 0 && <SectionCard title="Genuine strengths" icon={CheckCircle2}><Bullets items={data.strengths} /></SectionCard>}
      {data.how_to_validate?.length > 0 && <SectionCard title="How to validate"><Bullets items={data.how_to_validate} /></SectionCard>}
    </>
  );
  if (tool === "life-path") return (
    <>
      {data.goal_restated && <SectionCard title="Your goal" accent><p className="text-lg">{data.goal_restated}</p></SectionCard>}
      {data.summary && <p className="leading-relaxed">{data.summary}</p>}
      {data.skills?.length > 0 && <SectionCard title="Skills you'll build"><Chips items={data.skills} /></SectionCard>}
      {data.phases?.map((ph, i) => (
        <SectionCard key={i} title={`Phase ${i + 1}: ${ph.title}${ph.duration ? " · " + ph.duration : ""}`}>
          <Bullets items={ph.steps} />
          {ph.milestone && <p className="mt-3 text-sm"><b className="text-primary">Milestone:</b> {ph.milestone}</p>}
        </SectionCard>
      ))}
      {data.habits?.length > 0 && <SectionCard title="Daily / weekly habits"><Bullets items={data.habits} /></SectionCard>}
      {data.first_action && <SectionCard title="Do this today" icon={ArrowRight} accent><p>{data.first_action}</p></SectionCard>}
      {data.risks?.length > 0 && <SectionCard title="Watch out for"><Bullets items={data.risks} /></SectionCard>}
    </>
  );
  if (tool === "what-am-i-missing") return (
    <>
      {data.biggest_risk && <SectionCard title="Biggest risk" accent><p className="text-lg">{data.biggest_risk}</p></SectionCard>}
      {data.blind_spots?.length > 0 && <SectionCard title="Blind spots" icon={Search}><Bullets items={data.blind_spots} /></SectionCard>}
      {data.missing_prerequisites?.length > 0 && <SectionCard title="Missing prerequisites"><Bullets items={data.missing_prerequisites} /></SectionCard>}
      {data.unconsidered_alternatives?.length > 0 && <SectionCard title="Alternatives you haven't considered"><Bullets items={data.unconsidered_alternatives} /></SectionCard>}
      {data.key_questions?.length > 0 && <SectionCard title="Answer these before proceeding"><Bullets items={data.key_questions} /></SectionCard>}
    </>
  );
  if (tool === "make-it-fit") return (
    <>
      {data.why_this_fits && <SectionCard title="Why this fits you" accent><p>{data.why_this_fits}</p></SectionCard>}
      {data.personalised_plan && <SectionCard title="Your personalised plan"><p className="whitespace-pre-wrap leading-relaxed">{data.personalised_plan}</p></SectionCard>}
      {data.quick_wins?.length > 0 && <SectionCard title="Quick wins"><Bullets items={data.quick_wins} /></SectionCard>}
      {data.watch_outs?.length > 0 && <SectionCard title="Watch-outs"><Bullets items={data.watch_outs} /></SectionCard>}
      {data.first_step && <SectionCard title="First step" icon={ArrowRight} accent><p>{data.first_step}</p></SectionCard>}
    </>
  );
  return null;
}

export default function Tools() {
  const { mode } = useAppMode();
  const [active, setActive] = useState(TOOLS[0]);
  const [input, setInput] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!input.trim()) { toast.info("Describe your situation first."); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post(`/features/${active.key}`, { input: input.trim(), mode });
      if (data.error) { toast.error("Response unclear — try adding detail."); }
      else setResult(data);
    } catch { toast.error("Could not run this tool."); }
    finally { setLoading(false); }
  };

  const saveToVault = async () => {
    await api.post("/ideas", { title: `${active.label}: ${input.slice(0, 40)}`, data: { tool: active.key, input, result } });
    toast.success("Saved to your Idea Vault");
  };

  const addPathGoals = async () => {
    const titles = [];
    if (result?.first_action) titles.push(result.first_action);
    (result?.phases || []).forEach((ph) => { if (ph.milestone) titles.push(ph.milestone); else if (ph.title) titles.push(ph.title); });
    const { data } = await api.post("/goals/bulk", { titles, domain: "life-path" });
    toast.success(`Added ${data.added} goals to your Home dashboard`);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head font-extrabold text-3xl sm:text-4xl flex items-center gap-3"><Wand2 className="w-8 h-8 text-primary" />Life Tools</h1>
        <p className="text-muted-foreground mt-2">Signature engines that don't just tell you things — they stress-test your thinking.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {TOOLS.map((t) => (
          <button key={t.key} onClick={() => { setActive(t); setResult(null); setInput(""); }} data-testid={`tool-tab-${t.key}`}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold border-2 transition-colors ${active.key === t.key ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-border bg-card p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-2"><active.icon className="w-5 h-5 text-primary" /><h2 className="font-head font-bold text-lg">{active.label}</h2></div>
        <p className="text-sm text-muted-foreground mb-4">{active.tagline}</p>
        <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={4} data-testid="tool-input" placeholder={active.placeholder}
          className="w-full rounded-md border border-input bg-background px-3 py-3 outline-none focus:ring-2 focus:ring-ring resize-none" />
        <button onClick={run} disabled={loading} data-testid="tool-run" className="mt-3 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-bold px-5 py-2.5 hover:brightness-110 disabled:opacity-50 transition">
          <active.icon className="w-4 h-4" /> Run {active.label}
        </button>
      </div>

      {loading && <Loader label="Analysing deeply…" />}
      {result && (
        <div className="space-y-4">
          {Result({ tool: active.key, data: result })}
          <div className="flex flex-wrap gap-3">
            {active.key === "life-path" && (
              <button onClick={addPathGoals} data-testid="lifepath-to-goals" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-bold px-5 py-2.5 hover:brightness-110 transition"><Target className="w-4 h-4" />Add to my goals</button>
            )}
            <button onClick={saveToVault} data-testid="tool-save" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-semibold hover:bg-secondary"><Save className="w-4 h-4" />Save to Idea Vault</button>
          </div>
        </div>
      )}
    </div>
  );
}
